import { useState } from 'preact/hooks';
import type { Service } from '../../content/services.schema.js';
import { Icon } from '../Icon/Icon.js';

const tileBaseStyle = {
  minWidth: 0,
  borderRadius: '26px',
  padding: 'clamp(22px,3vw,30px)',
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'space-between',
  gap: '34px',
  minHeight: '232px',
};

const statusLabel: Record<Service['status'], string> = {
  live: '',
  coming_soon: 'COMING SOON',
  in_the_works: 'IN THE WORKS',
};

export function ServiceTile({ service, index }: { service: Service; index: number }) {
  const riseDelay = `${(0.18 + index * 0.06).toFixed(2)}s`;
  // cRise's `animation-fill-mode: both` keeps asserting `transform: none` at
  // CSS-animation cascade priority forever after it finishes — that outranks
  // any plain author rule (including our hover-driven `transform`), so the
  // Kereta tile's lift would be permanently blocked if the animation stayed
  // attached. Detach it once it completes: this also happens to satisfy
  // MOTION.md's "don't replay the stagger on re-render" requirement for free.
  const [hasEntered, setHasEntered] = useState(false);
  const entranceAnimation = hasEntered
    ? undefined
    : `cRise .6s ${riseDelay} cubic-bezier(.22,1,.36,1) both`;
  const onAnimationEnd = () => setHasEntered(true);

  if (service.status === 'live') {
    return (
      <a
        href={service.href ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        class="kereta-tile"
        onAnimationEnd={onAnimationEnd}
        style={{
          ...tileBaseStyle,
          position: 'relative',
          overflow: 'hidden',
          color: 'var(--ink)',
          border: '1px solid var(--yEdge)',
          backdropFilter: 'blur(var(--blur)) saturate(160%)',
          WebkitBackdropFilter: 'blur(var(--blur)) saturate(160%)',
          boxShadow: 'var(--cardShadowUp)',
          animation: entranceAnimation,
        }}
      >
        <div
          aria-hidden="true"
          class="kereta-sheen"
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: '55%',
            background: 'linear-gradient(100deg,transparent,var(--sheen),transparent)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <div
            style={{
              width: '50px',
              height: '50px',
              borderRadius: '15px',
              background: 'var(--yellow)',
              display: 'grid',
              placeItems: 'center',
              flex: 'none',
              boxShadow: 'var(--glowSm)',
            }}
          >
            <Icon name={service.icon} style={{ fontSize: '28px', color: '#0B1B2B' }} />
          </div>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              borderRadius: '999px',
              padding: '7px 12px',
              background: 'var(--pill)',
              color: 'var(--onPill)',
              font: "700 10.5px/1 'Plus Jakarta Sans',sans-serif",
              letterSpacing: '.1em',
            }}
          >
            <Icon name="bolt" style={{ fontSize: '13px' }} />
            LIVE NOW
          </div>
        </div>
        <div style={{ position: 'relative' }}>
          <div
            style={{
              font: "800 clamp(24px,2.9vw,29px)/1.06 'Plus Jakarta Sans',sans-serif",
              letterSpacing: '-.03em',
              color: 'var(--ink)',
            }}
          >
            {service.title}
          </div>
          <p
            style={{
              margin: '9px 0 0',
              font: "500 14px/1.5 'Plus Jakarta Sans',sans-serif",
              color: 'var(--muted)',
              textWrap: 'pretty',
            }}
          >
            {service.description}
          </p>
          {service.ctaLabel && (
            <div
              style={{
                marginTop: '18px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                font: "700 14px/1 'Plus Jakarta Sans',sans-serif",
                color: 'var(--ink)',
              }}
            >
              {service.ctaLabel}
              <Icon name="arrow_forward" style={{ fontSize: '17px' }} />
            </div>
          )}
        </div>
      </a>
    );
  }

  return (
    <div
      onAnimationEnd={onAnimationEnd}
      style={{
        ...tileBaseStyle,
        background: 'var(--glass)',
        border:
          service.status === 'in_the_works' ? '1px dashed var(--edge)' : '1px solid var(--edge)',
        backdropFilter: 'blur(var(--blur))',
        WebkitBackdropFilter: 'blur(var(--blur))',
        boxShadow: 'var(--cardShadow)',
        animation: entranceAnimation,
      }}
    >
      <div
        style={{
          width: '50px',
          height: '50px',
          borderRadius: '15px',
          background: 'var(--chip)',
          border: '1px solid var(--edge)',
          display: 'grid',
          placeItems: 'center',
          flex: 'none',
        }}
      >
        <Icon name={service.icon} style={{ fontSize: '28px', color: 'var(--faint)' }} />
      </div>
      <div>
        <div
          style={{
            font: "800 clamp(24px,2.9vw,29px)/1.06 'Plus Jakarta Sans',sans-serif",
            letterSpacing: '-.03em',
            color: 'var(--muted)',
          }}
        >
          {service.title}
        </div>
        <p
          style={{
            margin: '9px 0 0',
            font: "500 14px/1.5 'Plus Jakarta Sans',sans-serif",
            color: 'var(--faint)',
            textWrap: 'pretty',
          }}
        >
          {service.description}
        </p>
        <div
          style={{
            marginTop: '18px',
            font: "700 11.5px/1 'Plus Jakarta Sans',sans-serif",
            letterSpacing: '.11em',
            color: 'var(--faint)',
          }}
        >
          {statusLabel[service.status]}
        </div>
      </div>
    </div>
  );
}
